/**
 * Firebase Timestamp를 안전하게 Date로 변환하는 유틸리티
 * @param {*} timestamp - Firebase Timestamp, Date 객체, 또는 기타
 * @returns {Date|null} Date 객체 또는 null
 */
export const safeTimestampToDate = (timestamp) => {
  if (!timestamp) {
    return null;
  }
  
  // Firebase Timestamp 객체인 경우
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // 이미 Date 객체인 경우
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  // 문자열인 경우 (ISO 8601 등)
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // 숫자인 경우 (Unix timestamp)
  if (typeof timestamp === 'number') {
    return new Date(timestamp);
  }
  
  // seconds와 nanoseconds 속성이 있는 경우 (Firestore Timestamp 형태)
  if (timestamp && typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
  }
  
  console.warn('알 수 없는 timestamp 형태:', timestamp);
  return null;
};

/**
 * 날짜를 한국어 로케일로 포맷팅
 * @param {Date|*} date - Date 객체 또는 변환 가능한 값
 * @returns {string} 포맷된 날짜 문자열
 */
export const formatDate = (date) => {
  const safeDate = safeTimestampToDate(date);
  
  if (!safeDate) {
    return '날짜 정보 없음';
  }
  
  try {
    return safeDate.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (error) {
    console.error('날짜 포맷팅 오류:', error);
    return safeDate.toString();
  }
};

/**
 * 상대적 시간 표시 (예: "2분 전", "1시간 전")
 * @param {Date|*} date - Date 객체 또는 변환 가능한 값
 * @returns {string} 상대적 시간 문자열
 */
export const formatRelativeTime = (date) => {
  const safeDate = safeTimestampToDate(date);
  
  if (!safeDate) {
    return '알 수 없음';
  }
  
  const now = new Date();
  const diffMs = now.getTime() - safeDate.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSeconds < 60) {
    return '방금 전';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}분 전`;
  } else if (diffHours < 24) {
    return `${diffHours}시간 전`;
  } else if (diffDays < 7) {
    return `${diffDays}일 전`;
  } else {
    return formatDate(safeDate);
  }
};